/**
 * M4S-5777 X147: the Crosstabs list's calls to the portal API, outside the classic code.
 *
 * The same requests the classic list made through `src/@crosstab/services` (read-only, deleted after
 * the classic tool's quiet period), copied: list, rename, delete and the publish toggle. The list's
 * Delete is a soft delete the owner can restore for 30 days (crosstabs-access-lifecycle.md §3).
 * Creating and copying a crosstab are not here: an AI crosstab is created only by the workspace's
 * keyless save, and the classic create and copy belong to the classic tool.
 */

import { tracking } from '@/lib/features/tracking';
import { portalApi } from '@/lib/http/axios';

import type { CrosstabListParams, CrosstabListResponse } from './types';

/** The portal's crosstab endpoints, under `/studio/v1`. */
export const LIBRARY_ENDPOINTS = {
    LIST: '/crosstab/org/list',
    CROSSTAB: '/crosstab',
    PUBLISH: '/crosstab/publish',
} as const;

const unwrap = <T>(response: { data?: unknown }): T => (response.data ?? response) as T;

export const crosstabLibraryService = {
    async getOrgCrosstabs(params: CrosstabListParams): Promise<CrosstabListResponse> {
        try {
            const { data } = await portalApi.get(LIBRARY_ENDPOINTS.LIST, {
                params: {
                    projectId: params.projectId || '',
                    surveyId: params.surveyId || '',
                    page: params.page || 1,
                    search: params.search || '',
                    sortBy: params.sortBy || 'updatedAt',
                    sortOrder: params.sortOrder || 'desc',
                    ...(params.subsidiaryOrgIds?.length
                        ? { subsidiaryOrgIds: params.subsidiaryOrgIds.join(',') }
                        : {}),
                },
            });
            return unwrap<CrosstabListResponse>(data);
        } catch (error) {
            tracking.errorWithSource(error as Error, 'crosstabLibrary.getOrgCrosstabs');
            throw error;
        }
    },

    /** One saved crosstab as the portal answers it (its `aiLayout` is read by `ai-layout.ts`). */
    async getCrosstab(crosstabId: string): Promise<{ aiLayout?: unknown; [key: string]: unknown }> {
        try {
            const { data } = await portalApi.get(`${LIBRARY_ENDPOINTS.CROSSTAB}/${crosstabId}`);
            return unwrap(data);
        } catch (error) {
            tracking.errorWithSource(error as Error, 'crosstabLibrary.getCrosstab');
            throw error;
        }
    },

    async deleteCrosstab(crosstabId: string): Promise<unknown> {
        try {
            const { data } = await portalApi.delete(`${LIBRARY_ENDPOINTS.CROSSTAB}/${crosstabId}`);
            return unwrap(data);
        } catch (error) {
            tracking.errorWithSource(error as Error, 'crosstabLibrary.deleteCrosstab');
            throw error;
        }
    },

    async renameCrosstab(crosstabId: string, newName: string): Promise<unknown> {
        try {
            const { data } = await portalApi.patch(
                `${LIBRARY_ENDPOINTS.CROSSTAB}/${crosstabId}/rename`,
                { newName },
            );
            return unwrap(data);
        } catch (error) {
            tracking.errorWithSource(error as Error, 'crosstabLibrary.renameCrosstab');
            throw error;
        }
    },

    /** The list's publish toggle (the portal's; the workspace publishes through Studio's route). */
    async togglePublishCrosstab(crosstabId: string): Promise<unknown> {
        try {
            const { data } = await portalApi.patch(
                `${LIBRARY_ENDPOINTS.PUBLISH}/${crosstabId}`,
                {},
            );
            return unwrap(data);
        } catch (error) {
            tracking.errorWithSource(error as Error, 'crosstabLibrary.togglePublishCrosstab');
            throw error;
        }
    },
};
